import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Calendar, Eye, Tag, ChevronRight, ChevronLeft } from "lucide-react";
import { Navigation } from "./Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance for gesture
  const minSwipeDistance = 50;

  const nextPhoto = useCallback(() => {
    if (selectedPost?.photos) {
      setLightboxIndex((prev) => (prev + 1) % selectedPost.photos.length);
    }
  }, [selectedPost]);

  const prevPhoto = useCallback(() => {
    if (selectedPost?.photos) {
      setLightboxIndex((prev) => (prev - 1 + selectedPost.photos.length) % selectedPost.photos.length);
    }
  }, [selectedPost]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "ArrowRight") nextPhoto();
      if (e.key === "ArrowLeft") prevPhoto();
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, nextPhoto, prevPhoto]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) nextPhoto();
    if (isRightSwipe) prevPhoto();
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog`);
      setPosts(res.data.posts || []);
    } catch (err) {
      console.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const openPost = async (postId) => {
    try {
      const res = await axios.get(`${API}/blog/${postId}`);
      setSelectedPost(res.data);
    } catch (err) {
      console.error("Failed to load post");
    }
  };

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Navigation />
        <div className="pt-24 pb-20 px-6 max-w-4xl mx-auto">
          <button
            onClick={() => setSelectedPost(null)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 bg-transparent border-none cursor-pointer"
          >
            <ArrowLeft size={18} />
            <span className="font-body text-sm uppercase tracking-wider">Back to Portfolio</span>
          </button>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-wide mb-4">
              {selectedPost.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-500 font-body mb-8">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {new Date(selectedPost.created_at).toLocaleDateString("en-AU", { 
                  day: "numeric", month: "long", year: "numeric" 
                })}
              </span>
              <span className="text-[#3b82f6]">{selectedPost.vehicle}</span>
              <span className="text-zinc-600">|</span>
              <span>{selectedPost.service}</span>
              <span className="flex items-center gap-1 text-zinc-600">
                <Eye size={14} />
                {selectedPost.views || 0} views
              </span>
            </div>

            {/* Photo Gallery */}
            {selectedPost.photos && selectedPost.photos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                {selectedPost.photos.map((photo, i) => (
                  <button
                    key={i}
                    onClick={() => { setLightboxOpen(true); setLightboxIndex(i); }}
                    className="aspect-video bg-zinc-900 overflow-hidden border border-white/5 cursor-pointer p-0"
                  >
                    <img
                      src={photo}
                      alt={`${selectedPost.vehicle} - ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Description */}
            <div className="prose prose-invert max-w-none">
              {selectedPost.description.split("\n").map((para, i) => (
                <p key={i} className="text-zinc-300 font-body leading-relaxed mb-4">
                  {para}
                </p>
              ))}
            </div>

            {/* Tags */}
            {selectedPost.tags && selectedPost.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-white/10">
                {selectedPost.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 text-xs uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </motion.article>

          {/* CTA */}
          <div className="mt-16 p-8 bg-white/5 border border-white/10 text-center">
            <h3 className="font-heading text-xl uppercase tracking-wide mb-4">Want Similar Results?</h3>
            <p className="text-zinc-400 font-body mb-6">Get a free quote for your vehicle</p>
            <Link
              to="/book"
              className="inline-block px-8 py-3 bg-[#3b82f6] text-white font-heading text-sm uppercase tracking-wider hover:bg-[#3b82f6]/80 transition-colors no-underline"
            >
              Get a Quote
            </Link>
          </div>
        </div>

        {/* Lightbox with swipe and arrows */}
        {lightboxOpen && selectedPost.photos && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-white text-3xl bg-transparent border-none cursor-pointer z-10"
              onClick={() => setLightboxOpen(false)}
            >
              &times;
            </button>

            {/* Photo counter */}
            <div className="absolute top-4 left-4 text-white/60 text-sm font-mono">
              {lightboxIndex + 1} / {selectedPost.photos.length}
            </div>

            {/* Left Arrow */}
            {selectedPost.photos.length > 1 && (
              <button
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white cursor-pointer hover:bg-black/70 transition-colors z-10"
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
              >
                <ChevronLeft size={28} />
              </button>
            )}

            {/* Image */}
            <img
              src={selectedPost.photos[lightboxIndex]}
              alt=""
              className="max-w-[90vw] max-h-[85vh] object-contain select-none"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />

            {/* Right Arrow */}
            {selectedPost.photos.length > 1 && (
              <button
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white cursor-pointer hover:bg-black/70 transition-colors z-10"
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
              >
                <ChevronRight size={28} />
              </button>
            )}

            {/* Dots */}
            {selectedPost.photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {selectedPost.photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                    className={`w-2.5 h-2.5 rounded-full ${i === lightboxIndex ? "bg-white" : "bg-white/30"} border-none cursor-pointer transition-colors`}
                  />
                ))}
              </div>
            )}

            {/* Swipe hint for mobile */}
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/40 text-xs md:hidden">
              Swipe to navigate
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="pt-24 pb-20 px-6 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl uppercase tracking-wider mb-4">
            Our Work
          </h1>
          <p className="text-zinc-400 font-body text-lg max-w-2xl mx-auto">
            Featured builds, resprays, and transformations from our shop
          </p>
        </motion.div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/5 aspect-video animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-600 font-body">No posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="group cursor-pointer"
                onClick={() => openPost(post.id)}
              >
                <div className="aspect-video bg-zinc-900 overflow-hidden border border-white/5 mb-4">
                  {post.photos && post.photos[0] ? (
                    <img
                      src={post.photos[0]}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700">
                      No Image
                    </div>
                  )}
                </div>
                <h2 className="font-heading text-xl uppercase tracking-wide mb-2 group-hover:text-[#3b82f6] transition-colors">
                  {post.title}
                </h2>
                <div className="flex items-center gap-3 text-xs text-zinc-500 font-body">
                  <span className="text-[#3b82f6]">{post.vehicle}</span>
                  <span className="text-zinc-700">|</span>
                  <span>{post.service}</span>
                </div>
                <p className="text-zinc-500 font-body text-sm mt-3 line-clamp-2">
                  {post.description}
                </p>
                <div className="flex items-center gap-1 text-[#3b82f6] text-xs uppercase tracking-wider mt-4 font-body group-hover:gap-2 transition-all">
                  View Project <ChevronRight size={14} />
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-20 text-center"
        >
          <h2 className="font-heading text-3xl uppercase tracking-wide mb-4">Ready for Your Transformation?</h2>
          <p className="text-zinc-400 font-body mb-8">Get a free quote and join our portfolio of happy customers</p>
          <Link
            to="/book"
            className="inline-block px-10 py-4 bg-white text-black font-heading text-sm uppercase tracking-wider hover:bg-zinc-200 transition-colors no-underline"
          >
            Get a Quote
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPage;
